// Conversation thread for a repair OR tenancy anchor. Inbound + outbound
// messages, multi-channel. Read-only — pair with <MessageComposer />.

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDateGB } from "@/lib/format";
import { getSupabase } from "@/lib/supabase";
import { Mail, MessageSquareText, ShieldCheck, User, Wrench } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Direction = "in" | "out";
type SenderRole = "admin" | "tenant" | "contractor" | "agent" | "system";
type Source = "resend_inbound" | "portal_compose" | "automated_dispatch" | "agent_action";

export type Anchor = { kind: "repair"; id: string } | { kind: "tenancy"; id: string };

export type RepairMessage = {
  id: string;
  repair_id: string | null;
  tenancy_id: string | null;
  direction: Direction;
  sender_email: string;
  sender_name: string | null;
  sender_role: SenderRole;
  body_text: string | null;
  body_html: string | null;
  attachments: Array<{
    filename?: string;
    storage_path?: string;
    content_type?: string;
    size_bytes?: number;
  }>;
  source: Source;
  created_at: string;
};

export function MessageThread({
  anchor,
  title = "Conversation",
  description,
}: {
  anchor: Anchor;
  title?: string;
  description?: string;
}) {
  const [messages, setMessages] = useState<RepairMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    let cancelled = false;
    const sb = getSupabase();
    const column = anchor.kind === "repair" ? "repair_id" : "tenancy_id";

    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: fetchErr } = await sb
        .from("repair_messages")
        .select(
          "id, repair_id, tenancy_id, direction, sender_email, sender_name, sender_role, body_text, body_html, attachments, source, created_at",
        )
        .eq(column, anchor.id)
        .order("created_at", { ascending: true });
      if (cancelled) return;
      if (fetchErr) {
        setError(fetchErr.message);
      } else {
        setMessages((data ?? []) as RepairMessage[]);
      }
      setLoading(false);
    })();

    const channel = sb
      .channel(`msg-thread-${anchor.kind}-${anchor.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "repair_messages",
          filter: `${column}=eq.${anchor.id}`,
        },
        (payload) => {
          const next = payload.new as RepairMessage;
          setMessages((prev) => (prev.some((m) => m.id === next.id) ? prev : [...prev, next]));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void sb.removeChannel(channel);
    };
  }, [anchor.kind, anchor.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: messages.length is the trigger; scrolling reads from the ref imperatively.
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [messages.length]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-ink-dim">Loading conversation…</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>
        {error ? (
          <p role="alert" className="text-xs text-red-300 mb-2">
            {error}
          </p>
        ) : null}
        {messages.length === 0 ? (
          <p className="text-sm text-ink-dim">No messages yet.</p>
        ) : (
          <ol ref={scrollerRef} className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
            {messages.map((m) => (
              <MessageBubble key={m.id} m={m} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function MessageBubble({ m }: { m: RepairMessage }) {
  const isOutbound = m.direction === "out";
  const Icon = roleIcon(m.sender_role);
  const label = m.sender_name?.trim() || m.sender_email;
  const tag = roleLabel(m.sender_role);
  const sourceTag = sourceLabel(m.source);
  const text = (m.body_text ?? "").trim() || (m.body_html ? "(see HTML body)" : "");

  return (
    <li className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm border ${
          isOutbound ? "bg-accent/10 border-accent/30 text-ink" : "bg-bg-card border-line text-ink"
        }`}
      >
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ink-dim mb-1">
          <Icon size={12} className="shrink-0" />
          <span className="font-medium">{tag}</span>
          <span className="text-ink-dim/70">·</span>
          <span className="truncate">{label}</span>
          <span className="text-ink-dim/70">·</span>
          <span>{formatDateGB(m.created_at)}</span>
          {sourceTag ? (
            <>
              <span className="text-ink-dim/70">·</span>
              <span>{sourceTag}</span>
            </>
          ) : null}
        </div>
        <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{text}</p>
        {m.attachments && m.attachments.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-2">
            {m.attachments.map((a, i) => (
              <span
                key={a.storage_path ?? `${m.id}-att-${i}`}
                className="text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-line bg-bg-card text-ink-dim"
              >
                {a.filename ?? a.storage_path ?? `attachment-${i + 1}`}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

function roleIcon(role: SenderRole) {
  if (role === "admin") return ShieldCheck;
  if (role === "tenant") return User;
  if (role === "contractor") return Wrench;
  if (role === "agent") return MessageSquareText;
  return Mail;
}

function roleLabel(role: SenderRole): string {
  return role === "admin"
    ? "Trinium"
    : role === "tenant"
      ? "Tenant"
      : role === "contractor"
        ? "Contractor"
        : role === "agent"
          ? "Trinium Agent"
          : "System";
}

function sourceLabel(s: Source): string | null {
  if (s === "resend_inbound") return "via email";
  if (s === "portal_compose") return "via portal";
  if (s === "automated_dispatch") return "auto";
  if (s === "agent_action") return "agent";
  return null;
}
